/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Brand Identity & Company Profile Schemas & DTOs (Task 11.5.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Social media channels links schema.
 */
export const BrandSocialLinksSchema = z
  .object({
    facebook: z.string().optional().openapi({ example: 'https://facebook.com/sophiaplatform' }),
    twitter: z.string().optional().openapi({ example: 'https://x.com/sophiaplatform' }),
    linkedin: z.string().optional().openapi({ example: 'https://linkedin.com/company/sophiaplatform' }),
    github: z.string().optional().openapi({ example: 'https://github.com/sophiaplatform' }),
    youtube: z.string().optional().openapi({ example: 'https://youtube.com/@sophiaplatform' }),
  })
  .openapi('BrandSocialLinks');

export type BrandSocialLinks = z.infer<typeof BrandSocialLinksSchema>;

/**
 * Full Brand Identity & Corporate Profile Schema.
 */
export const BrandSettingsSchema = z
  .object({
    siteName: z.string().min(1).openapi({ example: 'Sophia Platform', description: 'Platform display name' }),
    siteDescription: z.string().openapi({ example: 'Edge-Native Generative AI & Creative SaaS Platform' }),
    slogan: z.string().optional().openapi({ example: 'Supercharged Edge AI Workspace' }),
    logoLightUrl: z.string().openapi({ example: '/assets/images/logo-light.svg' }),
    logoDarkUrl: z.string().openapi({ example: '/assets/images/logo-dark.svg' }),
    logoCollapsedUrl: z.string().optional().openapi({ example: '/assets/images/logo-icon.svg' }),
    faviconUrl: z.string().openapi({ example: '/favicon.ico' }),
    ogImageUrl: z.string().optional().openapi({ example: '/assets/images/og-preview.png' }),
    companyName: z.string().min(1).openapi({ example: 'Four Elements Corp' }),
    address: z.string().optional().openapi({ example: 'Innovation Hub, Ho Chi Minh City, Vietnam' }),
    contactEmail: z.string().email().openapi({ example: 'quangle@fourelementscorp.com' }),
    hotline: z.string().optional().openapi({ example: '+84 90 123 4567' }),
    taxId: z.string().optional().openapi({ example: '0316888999' }),
    copyrightText: z.string().openapi({ example: '© 2026 Four Elements Corp. All rights reserved.' }),
    socialLinks: BrandSocialLinksSchema,
    updatedAt: z.string().optional().openapi({ example: '2026-10-26T15:00:00Z' }),
  })
  .openapi('BrandSettings');

export type BrandSettings = z.infer<typeof BrandSettingsSchema>;

/**
 * Payload for updating brand settings.
 */
export const UpdateBrandSettingsInputSchema = BrandSettingsSchema.omit({
  updatedAt: true,
}).openapi('UpdateBrandSettingsInput');

export type UpdateBrandSettingsInput = z.infer<typeof UpdateBrandSettingsInputSchema>;

/**
 * Response schema for uploaded brand asset.
 */
export const BrandUploadResponseSchema = z
  .object({
    url: z.string().openapi({ example: 'https://media.sophiaplatform.com/brand/logo-light-a1b2c3d4.svg' }),
    key: z.string().openapi({ example: 'brand/logo-light-a1b2c3d4.svg' }),
    contentType: z.string().openapi({ example: 'image/svg+xml' }),
    size: z.number().openapi({ example: 4096 }),
  })
  .openapi('BrandUploadResponse');

export type BrandUploadResponse = z.infer<typeof BrandUploadResponseSchema>;
